import { Middleware, MiddlewareAPI, AnyAction } from '@reduxjs/toolkit';
import {
  updateConversationArchiveStatus,
  autoRestoreOnNewMessage,
  fetchArchivedConversations,
  fetchArchiveStats
} from './archiveSlice';

// WebSocket event types for archive operations
interface ArchiveWebSocketEvent {
  type: 'archive_update' | 'archive_restore' | 'archive_auto_restore' | 'archive_stats_update';
  data: any;
}

// Archive middleware to handle WebSocket events and side effects
export const archiveMiddleware: Middleware = (store) => (next) => (action: AnyAction) => {
  const result = next(action);
  
  // Handle WebSocket connection for archive updates
  if (action.type === 'auth/loginSuccess' || action.type === 'websocket/connected') {
    setupArchiveWebSocketHandlers(store);
  }
  
  // Handle auto-refresh after operations
  if (action.type?.endsWith('/fulfilled')) {
    const actionType = action.type.split('/')[1];
    if (['archiveConversation', 'restoreConversation', 'bulkArchive', 'bulkRestore'].includes(actionType)) {
      // Refresh stats after successful archive operations
      setTimeout(() => {
        (store as any).dispatch(fetchArchiveStats('both'));
      }, 1000);
    }
  }
  
  return result;
};

// Setup WebSocket handlers for archive events
function setupArchiveWebSocketHandlers(store: any) {
  // Get WebSocket instance (assuming it's available globally or through a service)
  const ws = (window as any).hospitalWebSocket;
  
  if (!ws) {
    if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
      console.warn('WebSocket not available for archive real-time updates');
    }
    return;
  }
  
  // Listen for archive-related WebSocket events
  const handleArchiveWebSocketMessage = (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      
      if (data.type?.startsWith('archive_')) {
        handleArchiveEvent(store, data as ArchiveWebSocketEvent);
      }
    } catch (error) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.error('Error parsing archive WebSocket message:', error);
      }
    }
  };
  
  ws.addEventListener('message', handleArchiveWebSocketMessage);
  
  // Cleanup function (store in global for cleanup)
  (window as any).cleanupArchiveWebSocket = () => {
    ws.removeEventListener('message', handleArchiveWebSocketMessage);
  };
}

// Handle specific archive WebSocket events
function handleArchiveEvent(store: any, event: ArchiveWebSocketEvent) {
  const { type, data } = event;
  
  switch (type) {
    case 'archive_update':
      // Someone archived a conversation
      if (data.conversation_id && data.archived !== undefined) {
        store.dispatch(updateConversationArchiveStatus({
          conversationId: data.conversation_id,
          isArchived: data.archived,
          archiveInfo: data.archive_info
        }));
        
        // Show notification if it wasn't the current user
        if (data.archived_by !== getCurrentUserId()) {
          showArchiveNotification(
            data.archived ? 'archived' : 'restored', 
            data.conversation_name,
            data.archived_by_name
          );
        }
      }
      break;
    
    case 'archive_restore':
      // Someone restored a conversation
      if (data.conversation_id) {
        store.dispatch(updateConversationArchiveStatus({
          conversationId: data.conversation_id,
          isArchived: false
        }));
        
        if (data.restored_by !== getCurrentUserId()) {
          showArchiveNotification('restored', data.conversation_name, data.restored_by_name);
        }
      }
      break;
    
    case 'archive_auto_restore':
      // Auto-restore triggered by new message
      if (data.conversation_id && data.message_id) {
        store.dispatch(autoRestoreOnNewMessage({
          conversationId: data.conversation_id,
          messageId: data.message_id
        }));
        
        showArchiveNotification('auto_restored', data.conversation_name);
      }
      break;
    
    case 'archive_stats_update':
      // Archive statistics have been updated
      store.dispatch(fetchArchiveStats('both'));
      break;
    
    default:
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.warn('Unknown archive WebSocket event type:', type);
      }
  }
}

// Utility functions
function getCurrentUserId(): string {
  // Get current user ID from localStorage for now
  return localStorage.getItem('hospital_user_id') || '';
}

function showArchiveNotification(action: string, conversationName?: string, userName?: string) {
  // Show toast notification for archive events
  const userDisplay = userName || 'Someone';
  const conversationDisplay = conversationName || 'this conversation';
  
  const messages = {
    archived: `${userDisplay} archived "${conversationDisplay}"`,
    restored: `${userDisplay} restored "${conversationDisplay}"`,
    auto_restored: `"${conversationDisplay}" was automatically restored due to new activity`
  };
  
  const message = messages[action as keyof typeof messages] || `Archive action: ${action}`;
  
  // Dispatch notification (assuming notification slice exists)
  if (globalStore) {
    globalStore.dispatch({
      type: 'notifications/addNotification',
      payload: {
        id: Date.now().toString(),
        type: 'info',
        title: 'Archive Update',
        message,
        duration: 5000
      }
    });
  }
}

// Archive WebSocket event creators for sending events
export const archiveWebSocketEvents = {
  // Notify others when we archive a conversation
  sendArchiveUpdate: (conversationId: string, archived: boolean, archiveInfo?: any, conversationName?: string, userName?: string) => {
    const ws = (window as any).hospitalWebSocket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      // Get user info from localStorage if not provided
      const currentUserName = userName || localStorage.getItem('hospital_user_name') || 'Someone';
      const currentUserId = getCurrentUserId();
      
      ws.send(JSON.stringify({
        type: 'archive_update',
        data: {
          conversation_id: conversationId,
          conversation_name: conversationName || 'Unknown Conversation',
          archived,
          archive_info: archiveInfo,
          archived_by: currentUserId,
          archived_by_name: currentUserName,
          timestamp: new Date().toISOString()
        }
      }));
    }
  },
  
  // Notify others when we restore a conversation
  sendRestoreUpdate: (conversationId: string, conversationName?: string, userName?: string) => {
    const ws = (window as any).hospitalWebSocket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      // Get user info from localStorage if not provided
      const currentUserName = userName || localStorage.getItem('hospital_user_name') || 'Someone';
      const currentUserId = getCurrentUserId();
      
      ws.send(JSON.stringify({
        type: 'archive_restore',
        data: {
          conversation_id: conversationId,
          conversation_name: conversationName || 'Unknown Conversation',
          restored_by: currentUserId,
          restored_by_name: currentUserName,
          timestamp: new Date().toISOString()
        }
      }));
    }
  },
  
  // Notify about bulk operations
  sendBulkArchiveUpdate: (conversationIds: string[], archived: boolean) => {
    const ws = (window as any).hospitalWebSocket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'archive_bulk_update',
        data: {
          conversation_ids: conversationIds,
          archived,
          archived_by: getCurrentUserId(),
          timestamp: new Date().toISOString()
        }
      }));
    }
  },
  
  // Request stats update
  requestStatsUpdate: () => {
    const ws = (window as any).hospitalWebSocket;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'archive_request_stats_update',
        data: {
          requested_by: getCurrentUserId(),
          timestamp: new Date().toISOString()
        }
      }));
    }
  }
};

// Global store reference for WebSocket events
let globalStore: any;